import type {
  SummarySpecV2,
  ChartSpecV2,
  TableSpecV2,
} from "@app/reports-v2/contracts/index";

/**
 * Presentation specs for the reservations report.
 *
 * Deliberately the same shapes the Data Room reports use, so this page renders
 * through ReportSummary/ReportChart/ReportTable and inherits data labels, the
 * validated palette, totals and empty states rather than growing its own
 * parallel chart code.
 */

/** Charts the user can switch the main plot between. */
export type ReservationView =
  | "over_time"
  | "status"
  | "source"
  | "weekday"
  | "hour"
  | "party_size"
  | "lead_time";

export const RESERVATION_VIEWS: Array<{
  value: ReservationView;
  labelKey: string;
}> = [
  { value: "over_time", labelKey: "reports.reservations.view.over_time" },
  { value: "status", labelKey: "reports.reservations.view.status" },
  { value: "source", labelKey: "reports.reservations.view.source" },
  { value: "weekday", labelKey: "reports.reservations.view.weekday" },
  { value: "hour", labelKey: "reports.reservations.view.hour" },
  { value: "party_size", labelKey: "reports.reservations.view.party_size" },
  { value: "lead_time", labelKey: "reports.reservations.view.lead_time" },
];

/**
 * KPI block. Headline answers "how much business and how big"; the second band
 * is the two rates that say whether that business actually turns up, each with
 * its denominator spelled out in the tooltip — an unexplained rate is the
 * fastest way for a report to mislead.
 */
export const reservationSummarySpec: SummarySpecV2 = {
  metricsPath: "metrics",
  metrics: [
    {
      key: "reservations",
      labelKey: "reports.reservations.m_reservations",
      format: "integer",
      emphasis: true,
      section: "reports.dataroom.section.headline",
      descriptionKey: "reports.reservations.d_reservations",
    },
    {
      key: "guests",
      labelKey: "reports.reservations.m_guests",
      format: "integer",
      emphasis: true,
      section: "reports.dataroom.section.headline",
      descriptionKey: "reports.reservations.d_guests",
    },
    {
      key: "avg_party_size",
      labelKey: "reports.reservations.m_avg_party",
      format: "decimal",
      emphasis: true,
      section: "reports.dataroom.section.headline",
      descriptionKey: "reports.reservations.d_avg_party",
    },
    {
      key: "avg_lead_days",
      labelKey: "reports.reservations.m_lead_time",
      format: "decimal",
      emphasis: true,
      section: "reports.dataroom.section.headline",
      descriptionKey: "reports.reservations.d_lead_time",
    },
    {
      key: "no_show_rate",
      labelKey: "reports.reservations.m_no_show_rate",
      format: "percentage",
      section: "reports.reservations.section.reliability",
      descriptionKey: "reports.reservations.d_no_show_rate",
    },
    {
      key: "cancellation_rate",
      labelKey: "reports.reservations.m_cancellation_rate",
      format: "percentage",
      section: "reports.reservations.section.reliability",
      descriptionKey: "reports.reservations.d_cancellation_rate",
    },
    {
      key: "unique_guests",
      labelKey: "reports.reservations.m_unique_guests",
      format: "integer",
      section: "reports.reservations.section.reliability",
      descriptionKey: "reports.reservations.d_unique_guests",
    },
    {
      key: "completed",
      labelKey: "reports.reservations.m_completed",
      format: "integer",
      section: "reports.reservations.section.reliability",
      descriptionKey: "reports.reservations.d_completed",
    },
  ],
};

/**
 * The same KPI block minus the two rates, used while a status filter is active.
 * Both rates are shares of a population the filter has already narrowed, so
 * they would report a tautology (filter to no-show, see a 100% no-show rate).
 */
export const filteredSummarySpec: SummarySpecV2 = {
  ...reservationSummarySpec,
  metrics: reservationSummarySpec.metrics.filter(
    (metric) => metric.format !== "percentage",
  ),
};

const countSeries = [
  {
    key: "reservations",
    labelKey: "reports.reservations.m_reservations",
    format: "integer" as const,
  },
  {
    key: "guests",
    labelKey: "reports.reservations.m_guests",
    format: "integer" as const,
  },
];

/**
 * One chart spec per view. Form follows the question:
 * - over time -> line, it is a trend
 * - status / source -> ranked horizontal bars with share-of-total
 * - weekday / hour -> vertical bars, the x-axis is an ordered cycle
 * - party size / lead time -> vertical bars over ordered buckets
 */
export const RESERVATION_CHARTS: Record<ReservationView, ChartSpecV2> = {
  over_time: {
    type: "line",
    dataPath: "series",
    x: "bucket",
    titleKey: "reports.reservations.chart.over_time",
    descriptionKey: "reports.reservations.chart.over_time_d",
    xLabelKey: "reports.reservations.axis.date",
    valueLabels: true,
    compactLabels: true,
    valueAxis: true,
    series: countSeries,
  },
  status: {
    type: "bar",
    orientation: "horizontal",
    dataPath: "by_status",
    x: "status_label",
    showPercentage: true,
    percentageTotalPath: "metrics.reservations",
    titleKey: "reports.reservations.chart.status",
    descriptionKey: "reports.reservations.chart.status_d",
    valueLabels: true,
    compactLabels: false,
    series: [countSeries[0]],
  },
  source: {
    type: "bar",
    orientation: "horizontal",
    dataPath: "by_source",
    x: "source_label",
    showPercentage: true,
    percentageTotalPath: "metrics.reservations",
    titleKey: "reports.reservations.chart.source",
    descriptionKey: "reports.reservations.chart.source_d",
    valueLabels: true,
    compactLabels: false,
    series: [countSeries[0]],
  },
  weekday: {
    type: "bar",
    dataPath: "by_weekday",
    x: "weekday_label",
    titleKey: "reports.reservations.chart.weekday",
    descriptionKey: "reports.reservations.chart.weekday_d",
    xLabelKey: "reports.reservations.axis.weekday",
    valueLabels: true,
    compactLabels: false,
    valueAxis: true,
    series: countSeries,
  },
  hour: {
    type: "bar",
    dataPath: "by_hour",
    x: "hour_label",
    titleKey: "reports.reservations.chart.hour",
    descriptionKey: "reports.reservations.chart.hour_d",
    xLabelKey: "reports.reservations.axis.hour",
    valueLabels: true,
    compactLabels: false,
    valueAxis: true,
    series: countSeries,
  },
  party_size: {
    type: "bar",
    dataPath: "by_party_size",
    x: "bucket",
    titleKey: "reports.reservations.chart.party_size",
    descriptionKey: "reports.reservations.chart.party_size_d",
    xLabelKey: "reports.reservations.axis.party_size",
    valueLabels: true,
    compactLabels: false,
    valueAxis: true,
    series: [countSeries[0]],
  },
  lead_time: {
    type: "bar",
    dataPath: "by_lead_time",
    x: "bucket_label",
    titleKey: "reports.reservations.chart.lead_time",
    descriptionKey: "reports.reservations.chart.lead_time_d",
    xLabelKey: "reports.reservations.axis.lead_time",
    valueLabels: true,
    compactLabels: false,
    valueAxis: true,
    series: [countSeries[0]],
  },
};

export const reservationTableSpec: TableSpecV2 = {
  rowKey: "id",
  showTotals: true,
  grainKey: "reports.reservations.grain",
  columns: [
    {
      key: "local_time",
      labelKey: "reports.reservations.col.time",
      format: "timestamp",
    },
    {
      key: "party_name",
      labelKey: "reports.reservations.col.guest",
      format: "text",
    },
    {
      key: "party_size",
      labelKey: "reports.reservations.col.party",
      format: "integer",
      align: "right",
      total: true,
    },
    {
      key: "status_label",
      labelKey: "reports.reservations.col.status",
      format: "text",
    },
    {
      key: "source_label",
      labelKey: "reports.reservations.col.source",
      format: "text",
      muted: true,
    },
    {
      key: "table_names",
      labelKey: "reports.reservations.col.tables",
      format: "text",
      muted: true,
    },
    {
      key: "lead_days",
      labelKey: "reports.reservations.col.lead_days",
      format: "integer",
      align: "right",
      muted: true,
    },
  ],
};
